import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  InputAdornment,
  Chip,
  Tooltip,
  Alert,
  Card,
  CardContent,
  Tabs,
  Tab,
  Badge,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Avatar,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  AssignmentReturn as ReturnIcon,
  Receipt as ReceiptIcon,
  CheckCircle as ApprovedIcon,
  HourglassEmpty as PendingIcon,
  Cancel as RejectedIcon,
  AttachMoney as MoneyIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Visibility as ViewIcon,
  Print as PrintIcon,
  CreditCard as CardIcon,
  AccountBalance as StoreIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  ShoppingCart as ProductIcon,
  SwapHoriz as ExchangeIcon,
} from "@mui/icons-material";

const SalesReturn = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [activeStep, setActiveStep] = useState(0);

  // Sample invoices for lookup
  const invoicesData = [
    {
      invoiceNo: "INV1001",
      date: "2024-01-20",
      customerName: "Rajesh Kumar",
      items: [
        { id: 1, productName: "Samsung Galaxy S23", code: "PROD001", qty: 1, price: 79999, tax: 18, returned: false },
        { id: 2, productName: "Nike Running Shoes", code: "PROD003", qty: 2, price: 4999, tax: 12, returned: false },
      ],
      grandTotal: 95036.92,
    },
    {
      invoiceNo: "INV1002",
      date: "2024-01-20",
      customerName: "Priya Sharma",
      items: [
        { id: 1, productName: "Dell Laptop i5", code: "PROD002", qty: 1, price: 54999, tax: 18, returned: false },
        { id: 2, productName: "Sony Headphones", code: "PROD007", qty: 1, price: 29999, tax: 18, returned: false },
      ],
      grandTotal: 100297.64,
    },
  ];

  // Sample return records
  const [returns, setReturns] = useState([
    {
      id: 1,
      returnNo: "RET001",
      invoiceNo: "INV1005",
      date: "2024-01-21",
      customerName: "Sneha Desai",
      items: 1,
      returnAmount: 4999,
      refundMethod: "Cash",
      returnReason: "Defective Product",
      status: "Approved",
      processedBy: "Admin User",
    },
    {
      id: 2,
      returnNo: "RET002",
      invoiceNo: "INV1003",
      date: "2024-01-21",
      customerName: "Walk-in Customer",
      items: 1,
      returnAmount: 3499,
      refundMethod: "Store Credit",
      returnReason: "Wrong Item Ordered",
      status: "Pending",
      processedBy: null,
    },
    {
      id: 3,
      returnNo: "RET003",
      invoiceNo: "INV1004",
      date: "2024-01-20",
      customerName: "Amit Patel",
      items: 2,
      returnAmount: 9998,
      refundMethod: "Card",
      returnReason: "Customer Changed Mind",
      status: "Approved",
      processedBy: "Admin User",
    },
  ]);

  // New return form state
  const [searchInvoiceNo, setSearchInvoiceNo] = useState("");
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [returnItems, setReturnItems] = useState([]);
  const [refundMethod, setRefundMethod] = useState("Cash");
  const [returnReason, setReturnReason] = useState("");
  const [notes, setNotes] = useState("");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [successMessage, setSuccessMessage] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [openNewReturnModal, setOpenNewReturnModal] = useState(false);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [viewingReturn, setViewingReturn] = useState(null);

  const steps = ["Search Invoice", "Select Items", "Return Details", "Confirm"];

  // Calculations for new return
  const returnSubtotal = returnItems.reduce((sum, item) => sum + (item.price * item.returnQty), 0);
  const returnTax = returnItems.reduce((sum, item) => {
    const itemTotal = item.price * item.returnQty;
    return sum + (itemTotal * item.tax / 100);
  }, 0);
  const refundAmount = returnSubtotal + returnTax;

  // Helper functions
  const getFilteredReturns = () => {
    let filtered = returns;

    if (statusFilter !== "all") {
      filtered = filtered.filter((ret) => ret.status === statusFilter);
    }

    if (activeTab === 1) {
      filtered = filtered.filter((ret) => ret.status === "Pending");
    } else if (activeTab === 2) {
      filtered = filtered.filter((ret) => ret.status === "Approved");
    } else if (activeTab === 3) {
      filtered = filtered.filter((ret) => ret.status === "Rejected");
    }

    return filtered;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Approved': return { bg: '#e8f5e9', color: '#2e7d32' };
      case 'Pending': return { bg: '#fff3e0', color: '#e65100' };
      case 'Rejected': return { bg: '#ffebee', color: '#d32f2f' };
      default: return { bg: '#f5f5f5', color: '#666' };
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  // Handlers
  const handleSearchInvoice = () => {
    const invoice = invoicesData.find(inv => inv.invoiceNo === searchInvoiceNo.toUpperCase());
    if (invoice) {
      setSelectedInvoice(invoice);
      setReturnItems(invoice.items.map(item => ({ ...item, returnQty: 0, selected: false })));
      setSuccessMessage(`Invoice ${invoice.invoiceNo} found!`);
      setTimeout(() => setSuccessMessage(""), 2000);
    } else {
      alert("Invoice not found. Please check the invoice number.");
    }
  };

  const handleItemSelect = (itemId, checked) => {
    setReturnItems(returnItems.map(item =>
      item.id === itemId
        ? { ...item, selected: checked, returnQty: checked ? item.qty : 0 }
        : item
    ));
  };

  const handleQuantityChange = (itemId, newQty) => {
    const item = returnItems.find(i => i.id === itemId);
    if (newQty < 0 || newQty > item.qty) return;

    setReturnItems(returnItems.map(item =>
      item.id === itemId ? { ...item, returnQty: newQty } : item
    ));
  };

  const handleNext = () => {
    if (activeStep === 0 && !selectedInvoice) {
      alert("Please search and select an invoice first");
      return;
    }
    if (activeStep === 1 && !returnItems.some(item => item.selected && item.returnQty > 0)) {
      alert("Please select at least one item to return");
      return;
    }
    if (activeStep === 2 && !returnReason) {
      alert("Please select a return reason");
      return;
    }
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmitReturn = () => {
    const newReturn = {
      id: returns.length + 1,
      returnNo: `RET${String(returns.length + 1).padStart(3, '0')}`,
      invoiceNo: selectedInvoice.invoiceNo,
      date: new Date().toISOString().split('T')[0],
      customerName: selectedInvoice.customerName,
      items: returnItems.filter(item => item.selected && item.returnQty > 0).length,
      returnAmount: refundAmount,
      refundMethod,
      returnReason,
      status: "Pending",
      processedBy: null,
    };

    setReturns([newReturn, ...returns]);
    setSuccessMessage("Return request submitted successfully!");
    setTimeout(() => setSuccessMessage(""), 3000);
    handleCloseNewReturn();
  };

  const handleOpenNewReturn = () => {
    setOpenNewReturnModal(true);
    setActiveStep(0);
    setSearchInvoiceNo("");
    setSelectedInvoice(null);
    setReturnItems([]);
    setReturnReason("");
    setRefundMethod("Cash");
    setNotes("");
  };

  const handleCloseNewReturn = () => {
    setOpenNewReturnModal(false);
    setActiveStep(0);
    setSearchInvoiceNo("");
    setSelectedInvoice(null);
    setReturnItems([]);
    setReturnReason("");
    setNotes("");
  };

  const handleView = (returnRecord) => {
    setViewingReturn(returnRecord);
    setOpenViewModal(true);
  };

  const handleApprove = (returnRecord) => {
    setReturns(returns.map(ret =>
      ret.id === returnRecord.id
        ? { ...ret, status: 'Approved', processedBy: 'Admin User' }
        : ret
    ));
    setSuccessMessage(`Return ${returnRecord.returnNo} approved!`);
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  const handleReject = (returnRecord) => {
    if (window.confirm(`Are you sure you want to reject return ${returnRecord.returnNo}?`)) {
      setReturns(returns.map(ret =>
        ret.id === returnRecord.id
          ? { ...ret, status: 'Rejected', processedBy: 'Admin User' }
          : ret
      ));
      setSuccessMessage(`Return ${returnRecord.returnNo} rejected!`);
      setTimeout(() => setSuccessMessage(""), 3000);
    }
  };

  const handleChangePage = (_, newPage) => setPage(newPage);
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredReturns = getFilteredReturns();

  // Calculate statistics
  const totalReturns = returns.length;
  const pendingReturns = returns.filter(r => r.status === "Pending").length;
  const approvedReturns = returns.filter(r => r.status === "Approved").length;
  const totalRefunded = returns.filter(r => r.status === "Approved").reduce((sum, r) => sum + r.returnAmount, 0);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ReturnIcon sx={{ color: 'primary.main', fontSize: 40 }} />
          <Box>
            <Typography variant="h4" fontWeight="bold" color="primary">
              Sales Return & Refund
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Process product returns and issue refunds • {filteredReturns.length} returns
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenNewReturn}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1,
            fontWeight: 600,
            backgroundColor: 'primary.main',
            '&:hover': {
              backgroundColor: 'primary.dark',
            },
          }}
        >
          New Return
        </Button>
      </Box>

      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
          {successMessage}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, boxShadow: 2, borderLeft: '4px solid #1976d2' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Total Returns</Typography>
                  <Typography variant="h4" fontWeight="bold" color="primary.main">
                    {totalReturns}
                  </Typography>
                </Box>
                <ReturnIcon sx={{ fontSize: 48, color: 'primary.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, boxShadow: 2, borderLeft: '4px solid #ff9800' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Pending</Typography>
                  <Typography variant="h4" fontWeight="bold" color="warning.main">
                    {pendingReturns}
                  </Typography>
                </Box>
                <PendingIcon sx={{ fontSize: 48, color: 'warning.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, boxShadow: 2, borderLeft: '4px solid #2e7d32' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Approved</Typography>
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    {approvedReturns}
                  </Typography>
                </Box>
                <ApprovedIcon sx={{ fontSize: 48, color: 'success.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, boxShadow: 2, borderLeft: '4px solid #d32f2f' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Total Refunded</Typography>
                  <Typography variant="h6" fontWeight="bold" color="error.main">
                    {formatCurrency(totalRefunded)}
                  </Typography>
                </Box>
                <MoneyIcon sx={{ fontSize: 48, color: 'error.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3, borderRadius: 2 }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab 
            label={<Badge badgeContent={totalReturns} color="primary">All Returns</Badge>} 
            icon={<ReturnIcon />} 
            iconPosition="start" 
          />
          <Tab 
            label={<Badge badgeContent={pendingReturns} color="warning">Pending</Badge>} 
            icon={<PendingIcon />} 
            iconPosition="start" 
          />
          <Tab 
            label={<Badge badgeContent={approvedReturns} color="success">Approved</Badge>} 
            icon={<ApprovedIcon />} 
            iconPosition="start" 
          />
          <Tab 
            label={<Badge badgeContent={returns.filter(r => r.status === "Rejected").length} color="error">Rejected</Badge>} 
            icon={<RejectedIcon />} 
            iconPosition="start" 
          />
        </Tabs>
      </Paper>

      {/* Table Section */}
      <Paper sx={{ borderRadius: 2, boxShadow: 3, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Return No</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Invoice No</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Customer</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Items</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Refund Amount</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Refund Method</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Reason</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredReturns.length > 0 ? (
                filteredReturns.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((returnRecord) => {
                  const statusStyle = getStatusColor(returnRecord.status);
                  return (
                    <TableRow key={returnRecord.id} sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="700" sx={{ fontFamily: 'monospace' }}>
                          {returnRecord.returnNo}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {returnRecord.invoiceNo}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CalendarIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2">{formatDate(returnRecord.date)}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ bgcolor: 'error.main', width: 32, height: 32, fontSize: '0.875rem' }}>
                            {returnRecord.customerName.charAt(0)}
                          </Avatar>
                          <Typography variant="body2" fontWeight="600">{returnRecord.customerName}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip label={`${returnRecord.items} items`} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="700" color="error">
                          {formatCurrency(returnRecord.returnAmount)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={returnRecord.refundMethod} size="small" color="info" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {returnRecord.returnReason}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={returnRecord.status}
                          size="small"
                          sx={{
                            backgroundColor: statusStyle.bg,
                            color: statusStyle.color,
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <Tooltip title="View Details">
                            <IconButton size="small" onClick={() => handleView(returnRecord)} sx={{ color: 'info.main' }}>
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {returnRecord.status === "Pending" && (
                            <>
                              <Tooltip title="Approve">
                                <IconButton size="small" onClick={() => handleApprove(returnRecord)} sx={{ color: 'success.main' }}>
                                  <ApprovedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Reject">
                                <IconButton size="small" onClick={() => handleReject(returnRecord)} sx={{ color: 'error.main' }}>
                                  <RejectedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                          {returnRecord.status === "Approved" && (
                            <Tooltip title="Print Receipt">
                              <IconButton size="small" sx={{ color: 'primary.main' }}>
                                <PrintIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <ReturnIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body1" color="text.secondary">No returns found</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredReturns.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>

      {/* New Return Modal with Stepper */}
      <Dialog open={openNewReturnModal} onClose={handleCloseNewReturn} maxWidth="md" fullWidth>
        <DialogTitle sx={{
          backgroundColor: 'primary.main',
          color: 'white',
        }}>
          <Typography variant="h6" sx={{ color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReturnIcon /> New Sales Return
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Step 0: Search Invoice */}
          {activeStep === 0 && (
            <Box>
              <Alert severity="info" sx={{ mb: 3 }}>
                Enter the invoice number to initiate a return
              </Alert>
              <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    label="Invoice Number"
                    value={searchInvoiceNo}
                    onChange={(e) => setSearchInvoiceNo(e.target.value)}
                    placeholder="Enter invoice number (e.g., INV1001)"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <ReceiptIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleSearchInvoice}
                    startIcon={<SearchIcon />}
                    sx={{ height: 56 }}
                  >
                    Search
                  </Button>
                </Grid>
              </Grid>

              {selectedInvoice && (
                <Paper sx={{ p: 2, mt: 3, bgcolor: '#f5f5f5' }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Invoice Found
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Invoice No:</Typography>
                      <Typography variant="body1" fontWeight="600">{selectedInvoice.invoiceNo}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Date:</Typography>
                      <Typography variant="body1" fontWeight="600">{formatDate(selectedInvoice.date)}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Customer:</Typography>
                      <Typography variant="body1" fontWeight="600">{selectedInvoice.customerName}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Total Amount:</Typography>
                      <Typography variant="body1" fontWeight="600" color="primary">
                        {formatCurrency(selectedInvoice.grandTotal)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              )}
            </Box>
          )}

          {/* Step 1: Select Items */}
          {activeStep === 1 && (
            <Box>
              <Alert severity="warning" sx={{ mb: 3 }}>
                Select the items you want to return and specify quantities
              </Alert>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Select</TableCell>
                      <TableCell>Product</TableCell>
                      <TableCell>Price</TableCell>
                      <TableCell>Purchased Qty</TableCell>
                      <TableCell>Return Qty</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {returnItems.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <Checkbox
                            checked={item.selected}
                            onChange={(e) => handleItemSelect(item.id, e.target.checked)}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="600">{item.productName}</Typography>
                          <Typography variant="caption" color="text.secondary">{item.code}</Typography>
                        </TableCell>
                        <TableCell>{formatCurrency(item.price)}</TableCell>
                        <TableCell>{item.qty}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <IconButton
                              size="small"
                              onClick={() => handleQuantityChange(item.id, item.returnQty - 1)}
                              disabled={!item.selected || item.returnQty === 0}
                            >
                              <RemoveIcon fontSize="small" />
                            </IconButton>
                            <Typography variant="body2" sx={{ minWidth: 30, textAlign: 'center' }}>
                              {item.returnQty}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => handleQuantityChange(item.id, item.returnQty + 1)}
                              disabled={!item.selected || item.returnQty === item.qty}
                            >
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Step 2: Return Details */}
          {activeStep === 2 && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Return Reason *</InputLabel>
                    <Select
                      value={returnReason}
                      onChange={(e) => setReturnReason(e.target.value)}
                      label="Return Reason *"
                    >
                      <MenuItem value="Defective Product">Defective Product</MenuItem>
                      <MenuItem value="Wrong Item Ordered">Wrong Item Ordered</MenuItem>
                      <MenuItem value="Customer Changed Mind">Customer Changed Mind</MenuItem>
                      <MenuItem value="Size/Fit Issue">Size/Fit Issue</MenuItem>
                      <MenuItem value="Damaged During Shipping">Damaged During Shipping</MenuItem>
                      <MenuItem value="Not as Described">Not as Described</MenuItem>
                      <MenuItem value="Better Price Available">Better Price Available</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Refund Method</InputLabel>
                    <Select
                      value={refundMethod}
                      onChange={(e) => setRefundMethod(e.target.value)}
                      label="Refund Method"
                    >
                      <MenuItem value="Cash">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <MoneyIcon /> Cash
                        </Box>
                      </MenuItem>
                      <MenuItem value="Card">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CardIcon /> Card (Original Payment Method)
                        </Box>
                      </MenuItem>
                      <MenuItem value="Store Credit">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <StoreIcon /> Store Credit
                        </Box>
                      </MenuItem>
                      <MenuItem value="Exchange">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <ExchangeIcon /> Exchange for Another Product
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Additional Notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Add any additional information about this return"
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Step 3: Confirm */}
          {activeStep === 3 && (
            <Box>
              <Alert severity="success" sx={{ mb: 3 }}>
                Please review the return details before submitting
              </Alert>
              <Paper sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5' }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>Return Summary</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Invoice:</Typography>
                    <Typography variant="body1" fontWeight="600">{selectedInvoice?.invoiceNo}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Customer:</Typography>
                    <Typography variant="body1" fontWeight="600">{selectedInvoice?.customerName}</Typography>
                  </Grid>
                </Grid>
              </Paper>

              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Items to Return:</Typography>
              {returnItems.filter(item => item.selected && item.returnQty > 0).map((item) => (
                <Paper key={item.id} sx={{ p: 1.5, mb: 1, bgcolor: '#f5f5f5' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">{item.productName} × {item.returnQty}</Typography>
                    <Typography variant="body2" fontWeight="600">
                      {formatCurrency(item.price * item.returnQty)}
                    </Typography>
                  </Box>
                </Paper>
              ))}

              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Subtotal:</Typography>
                <Typography variant="body2" fontWeight="600">{formatCurrency(returnSubtotal)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Tax:</Typography>
                <Typography variant="body2" fontWeight="600">{formatCurrency(returnTax)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 2, bgcolor: '#e3f2fd', borderRadius: 1 }}>
                <Typography variant="h6" fontWeight="bold">Refund Amount:</Typography>
                <Typography variant="h6" fontWeight="bold" color="primary">
                  {formatCurrency(refundAmount)}
                </Typography>
              </Box>

              <Paper sx={{ p: 2, mt: 2, bgcolor: '#fff3e0' }}>
                <Typography variant="caption" color="text.secondary">Return Reason:</Typography>
                <Typography variant="body2" fontWeight="600">{returnReason}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Refund Method:
                </Typography>
                <Typography variant="body2" fontWeight="600">{refundMethod}</Typography>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button onClick={handleCloseNewReturn}>Cancel</Button>
          {activeStep > 0 && (
            <Button onClick={handleBack}>Back</Button>
          )}
          {activeStep < steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={handleNext}
              sx={{
                backgroundColor: 'primary.main',
                '&:hover': { backgroundColor: 'primary.dark' },
              }}
            >
              Next
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleSubmitReturn}
              sx={{
                backgroundColor: 'success.main',
                '&:hover': { backgroundColor: 'success.dark' },
              }}
            >
              Submit Return
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* View Return Modal */}
      <Dialog open={openViewModal} onClose={() => setOpenViewModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{
          backgroundColor: 'primary.main',
          color: 'white',
        }}>
          <Typography variant="h6" sx={{ color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon /> Return Details
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {viewingReturn && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Return No:</Typography>
                      <Typography variant="body1" fontWeight="700">{viewingReturn.returnNo}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Status:</Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <Chip
                          label={viewingReturn.status}
                          sx={{
                            bgcolor: getStatusColor(viewingReturn.status).bg,
                            color: getStatusColor(viewingReturn.status).color,
                          }}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Invoice No:</Typography>
                      <Typography variant="body1" fontWeight="600">{viewingReturn.invoiceNo}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Date:</Typography>
                      <Typography variant="body1" fontWeight="600">{formatDate(viewingReturn.date)}</Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Customer:</Typography>
                <Typography variant="body1" fontWeight="600">{viewingReturn.customerName}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Paper sx={{ p: 2, bgcolor: 'rgba(244, 67, 54, 0.05)', textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">Refund Amount</Typography>
                  <Typography variant="h4" fontWeight="bold" color="error.main">
                    {formatCurrency(viewingReturn.returnAmount)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Refund Method:</Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip label={viewingReturn.refundMethod} color="info" />
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Items Returned:</Typography>
                <Typography variant="body1" fontWeight="600">{viewingReturn.items}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Return Reason:</Typography>
                <Typography variant="body1">{viewingReturn.returnReason}</Typography>
              </Grid>
              {viewingReturn.processedBy && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Processed By:</Typography>
                  <Typography variant="body1" fontWeight="600">{viewingReturn.processedBy}</Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setOpenViewModal(false)}>Close</Button>
          {viewingReturn?.status === "Approved" && (
            <Button
              variant="contained"
              startIcon={<PrintIcon />}
              sx={{
                backgroundColor: 'primary.main',
                '&:hover': { backgroundColor: 'primary.dark' },
              }}
            >
              Print Receipt
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SalesReturn;

